import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ChangeEvent} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import {writeQuery} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {Header1} from 'ui-library/Headers';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {SettingsLabel} from 'ui-library/SettingsField';

import {InventorySettingsQuery} from './__generated/InventorySettingsQuery';
import {InventoryConfigParams, updateInventoryConfig} from './InventoryDAO';

export const INVENTORY_SETTINGS_QUERY = gql`
  query InventorySettingsQuery @skipAudit @engComponent(name: INVENTORY) {
    currentTenant {
      id
      name
      tenantAdminData {
        ... on TenantAdminDataNode {
          id
          configOverrides(keys: ["INVENTORY_ENABLE_ARCHIVE_ENTITY_IN_CONTAINER_OPTION"]) {
            id
          }
        }
      }
      permissions {
        admin
      }
    }
    inventoryConfig {
      ... on InventoryConfigNode {
        id
        enableArchiveEntityInContainerOption
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

function InventorySettingsPageContent({
  conf,
  onPersistChanges,
}: {
  conf: InventorySettingsQuery;
  onPersistChanges: (settings: InventoryConfigParams) => Promise<void>;
}): JSX.Element {
  const handleChangeEnableArchiveEntityInContainerOption = (e: ChangeEvent<HTMLInputElement>): void => {
    const newEnableArchiveEntityInContainerOption = Boolean(e.target.checked);
    writeQuery(
      {query: INVENTORY_SETTINGS_QUERY},
      {
        ...conf,
        inventoryConfig: {
          enableArchiveEntityInContainerOption: newEnableArchiveEntityInContainerOption,
        },
      }
    );
    onPersistChanges({
      enableArchiveEntityInContainerOption: newEnableArchiveEntityInContainerOption,
    });
  };

  return (
    <div>
      {!isInaccessible(conf.inventoryConfig) && (
        <div className={css(styles.settingsPageSection)}>
          <SettingsLabel label="Archiving Containers" />
          <CheckboxInput
            id="enable-archive-entity-option"
            checked={Boolean(conf.inventoryConfig.enableArchiveEntityInContainerOption)}
            onChange={handleChangeEnableArchiveEntityInContainerOption}
            disabled={!conf.currentTenant.permissions.admin}
            label="When a container is archived, choose to also archive the entity it stores."
            helpText="Enabling this feature displays an option to archive the entity stored in a container being archived, and removes the archived entity from all other containers that store it."
          />
        </div>
      )}
    </div>
  );
}

export default function InventorySettingsPage(): JSX.Element {
  setWindowTitle('General Inventory Settings');

  const queryResult = useBenchlingQuery<InventorySettingsQuery>(INVENTORY_SETTINGS_QUERY, {
    renderCachedDataWhenRefetching: true,
  });

  const handlePersistChanges = async (settings: InventoryConfigParams): Promise<void> => {
    try {
      await updateInventoryConfig(settings);
      notifications.success('Inventory settings successfully updated.');
    } finally {
      queryResult.refetch();
    }
  };

  const mainContent = getMainContent();

  return (
    <div className="settingsPageContainer-scrollContainer">
      <div className="settingsPage">
        <div className="settingsPage-contentWrapper">
          <div className="u-lr-split">
            <Header1 className={classNames('settingsPage-header', css(styles.settingsPageHeader))}>
              General inventory settings
            </Header1>
          </div>
          {mainContent}
        </div>
      </div>
    </div>
  );

  function getMainContent(): JSX.Element {
    if (queryResult.type === QueryResultType.ERROR) {
      const message = 'Unable to load inventory configuration. Please try again.';
      notifications.error(message);
      return <p>{message}</p>;
    } else if (queryResult.type === QueryResultType.SUCCESS) {
      return <InventorySettingsPageContent conf={queryResult.data} onPersistChanges={handlePersistChanges} />;
    }
    return <HorizontalThrobber />;
  }
}

const styles = StyleSheet.create({
  settingsPageHeader: {
    marginBottom: '8px',
  },
  settingsPageSection: {
    marginTop: '18px',
  },
});
